import { useEffect, useState } from 'react';
import { useSubscriptionContext } from './Subscription.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import { COLLECTION_METHOD_SEND_INVOICE } from './useSeatPurchase.functions';
import { LLog } from 'src/lib/logging';

/**
 * Whether this workspace needs to hand over a card before a seat can be charged.
 */
export enum eSeatPaymentState {
  /** Still looking. Submit waits on this rather than guessing. */
  PENDING = 'PENDING',
  /** A usable card is on file; nothing to ask for. */
  HAS_CARD = 'HAS_CARD',
  /** Card-billed with no usable card — this is who card capture exists for. */
  NEEDS_CARD = 'NEEDS_CARD',
  /** `send_invoice` terms. Never ask for a card (R7/KD4). */
  INVOICE_BILLED = 'INVOICE_BILLED',
  /**
   * The lookup failed. Deliberately NOT the same as "no card": attempt the purchase
   * and let the resolved error copy handle a genuine payment-method problem, rather
   * than demanding card details the customer may not need to re-enter.
   */
  UNKNOWN = 'UNKNOWN',
}

const LOG_CONTEXT = {
  component: 'useSeatPaymentMethod',
  method: 'useSeatPaymentMethod',
  operation: 'payment-method-lookup',
};

/**
 * The billing-mode and payment-method lookup behind card capture.
 *
 * Fired in the background when the flow opens at the limit so it resolves while the
 * customer types — the form is never blocked on it — and gated on `enabled` so a
 * workspace with a free seat issues no lookup at all (R12).
 */
export function useSeatPaymentMethod(options: {
  enabled: boolean;
}): { state: eSeatPaymentState; isResolved: boolean } {
  const { enabled } = options;
  const { subscription } = useSubscriptionContext();
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';

  const [state, setState] = useState<eSeatPaymentState>(
    eSeatPaymentState.PENDING,
  );

  const isInvoiceBilled =
    subscription?.subscriptionCollectionMethod === COLLECTION_METHOD_SEND_INVOICE;
  const isCardBilled =
    subscription?.subscriptionCollectionMethod === 'charge_automatically';

  useEffect(() => {
    if (!enabled || !workspaceId) {
      setState(eSeatPaymentState.PENDING);
      return;
    }

    // Invoice terms settle without a network call: there is no card to look for, and
    // asking for one would contradict what the notice just promised.
    if (isInvoiceBilled) {
      setState(eSeatPaymentState.INVOICE_BILLED);
      return;
    }

    if (!isCardBilled) {
      setState(eSeatPaymentState.UNKNOWN);
      return;
    }

    let cancelled = false;
    setState(eSeatPaymentState.PENDING);

    SubscriptionManagerService.getPaymentMethods(workspaceId)
      .then((response) => {
        if (cancelled) return;
        const hasUsableCard = (response?.paymentMethods?.length ?? 0) > 0;
        setState(
          hasUsableCard
            ? eSeatPaymentState.HAS_CARD
            : eSeatPaymentState.NEEDS_CARD,
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Unknown, not "no card". Forcing card entry on a failed lookup would make
        // every transient error look like a billing problem the customer caused.
        setState(eSeatPaymentState.UNKNOWN);
        LLog.error(
          LOG_CONTEXT,
          'Could not resolve the payment method for a seat purchase',
          error instanceof Error ? error : undefined,
          { workspaceId },
        );
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, isInvoiceBilled, isCardBilled]);

  return {
    state,
    // Submit gates on this. UNKNOWN counts as resolved: we are done looking, and the
    // purchase attempt itself is the next honest step.
    isResolved: !enabled || state !== eSeatPaymentState.PENDING,
  };
}
